<script setup lang="ts">
import type { Components } from '@/api/generated/api';
import ErrorBadge from '@/core/badges/ErrorBadge.vue';
import AddButton from '@/core/elements/AddButton.vue';
import CustomButton from '@/core/elements/CustomButton.vue';
import FormStudent from '@/core/forms/FormStudent.vue';
import { IOnboardingStudent } from '@/interfaces';
import { createDraftStudent } from '@/utils/students.utils';
import { isNotNullish } from 'common/utils/array.utils';
import { computed, nextTick, onMounted, ref, watch } from 'vue';

const props = defineProps<{
  initialStudents?: IOnboardingStudent[];
  /** Shown as a link instead of the add button while there are no rows. */
  emptyAddLabel?: string;
}>();

const emit = defineEmits<{
  'update:students': [value: IOnboardingStudent[]];
  'update:isValid': [value: boolean];
}>();

const studentRows = ref<{ key: string; student: IOnboardingStudent | null }[]>([]);
const students = computed(() => studentRows.value.map((row) => row.student).filter(isNotNullish));
const validationError = ref(false);
const studentForms = ref<InstanceType<typeof FormStudent>[]>([]);
// a list without an empty state keeps its last row
const canRemoveRows = computed(() => studentRows.value.length > 1 || !!props.emptyAddLabel);

const validate = (): boolean => {
  validationError.value = students.value.some((student) => !student.firstName.trim() || !student.lastName.trim());
  return !validationError.value;
};

const handleStudentUpdate = (key: string, student: IOnboardingStudent | null): void => {
  validationError.value = false;
  if (student) {
    studentRows.value = studentRows.value.map((row) => (row.key === key ? { ...row, student } : row));
  } else {
    studentRows.value = studentRows.value.filter((row) => row.key !== key);
  }
};

const addRow = async (): Promise<void> => {
  studentRows.value = [...studentRows.value, { key: crypto.randomUUID(), student: null }];
  await nextTick(() => {
    studentForms.value.at(-1)?.firstNameInput?.focus();
  });
};

const handleEnterPressed = async (key: string): Promise<void> => {
  if (key === studentRows.value.at(-1)?.key) await addRow();
};

// Tab leaves an empty last row, so the footer stays reachable by keyboard
const handleTabPressed = async (key: string, event: KeyboardEvent): Promise<void> => {
  const lastRow = studentRows.value.at(-1);
  if (key !== lastRow?.key || !(lastRow.student?.firstName.trim() || lastRow.student?.lastName.trim())) return;
  event.preventDefault();
  await addRow();
};

/** Rows of an uploaded list follow the typed ones and take the place of empty rows. */
const addStudents = (rows: Components.Schemas.StudentImportRowDto[]): void => {
  validationError.value = false;
  studentRows.value = [
    ...studentRows.value.filter((row) => row.student),
    ...rows.map((row) => ({ key: crypto.randomUUID(), student: createDraftStudent(row) })),
  ];
};

const reset = (): void => {
  studentRows.value = [];
  validationError.value = false;
};

onMounted(() => {
  studentRows.value = (props.initialStudents ?? []).map((student) => ({ key: crypto.randomUUID(), student }));
});

watch(validationError, () => emit('update:isValid', !validationError.value), { immediate: true });

watch(students, () => emit('update:students', students.value));

defineExpose({ addRow, addStudents, validate, reset });
</script>

<template>
  <div class="flex min-w-0 flex-col gap-sm">
    <div v-for="row in studentRows" :key="row.key" class="bg-surface-secondary rounded-group pl-md pr-smd py-smd">
      <FormStudent
        ref="studentForms"
        :model-value="row.student"
        :removable="canRemoveRows"
        allow-picture-upload
        @update:model-value="handleStudentUpdate(row.key, $event)"
        @enter-pressed="handleEnterPressed(row.key)"
        @tab-pressed="handleTabPressed(row.key, $event)"
      />
    </div>
    <ErrorBadge v-if="validationError" :label="$t('onboarding.students.validationError')" />
    <slot />
    <CustomButton
      v-if="!studentRows.length && emptyAddLabel"
      theme="link"
      class="w-full md:w-min md:whitespace-nowrap px-sm"
      @click="addRow"
    >
      {{ emptyAddLabel }}
    </CustomButton>
    <AddButton
      v-else
      theme="text"
      :label="$t('onboarding.students.addStudent')"
      class="w-min whitespace-nowrap px-sm"
      @click="addRow"
    />
  </div>
</template>
